import { slugifyListKey } from './utils'

export const optionSetKey = (templateUniqueId, questionId) =>
    `${templateUniqueId}-${questionId}`

export const normalizeOptionSet = raw => {
    if (!raw) return null
    const items = (raw.Items ?? raw.items ?? []).map((it, idx) => ({
        displayText: it.DisplayText ?? it.displayText ?? '',
        score: it.Score ?? it.score ?? 0,
        displayOrder: it.DisplayOrder ?? it.displayOrder ?? idx,
        backColorCode: it.BackColorCode ?? it.backColorCode ?? '',
        foreColorCode: it.ForeColorCode ?? it.foreColorCode ?? '',
        helpText: it.HelpText ?? it.helpText ?? '',
        isExcludedFromScoring:
            it.IsExcludedFromScoring ?? it.isExcludedFromScoring ?? false,
        allowManualScore: it.AllowManualScore ?? it.allowManualScore ?? false,
        maxManualScore: it.MaxManualScore ?? it.maxManualScore ?? null,
        isScoreFromQuestion:
            it.IsScoreFromQuestion ?? it.isScoreFromQuestion ?? false,
    }))
    return {
        templateUniqueId: raw.TemplateUniqueId ?? raw.templateUniqueId,
        templateName: raw.TemplateName ?? raw.templateName ?? '',
        questionId: raw.QuestionId ?? raw.questionId,
        questionText: raw.QuestionText ?? raw.questionText ?? '',
        groupId: raw.GroupId ?? raw.groupId,
        items,
        key: optionSetKey(
            raw.TemplateUniqueId ?? raw.templateUniqueId,
            raw.QuestionId ?? raw.questionId,
        ),
    }
}

export const normalizeOptionSets = data => {
    const rows = Array.isArray(data) ? data : data?.Items ?? []
    return rows.map(normalizeOptionSet).filter(Boolean)
}

export const normalizeMigrationHistory = raw => {
    if (!raw) return null
    return {
        id: raw.Id ?? raw.id,
        masterListId: raw.MasterListId ?? raw.masterListId,
        companyId: raw.CompanyId ?? raw.companyId,
        masterListName: raw.MasterListName ?? raw.masterListName ?? '',
        masterListKey: raw.MasterListKey ?? raw.masterListKey ?? '',
        itemsInserted: raw.ItemsInserted ?? raw.itemsInserted ?? 0,
        questionsUpdated: raw.QuestionsUpdated ?? raw.questionsUpdated ?? 0,
        committedBy: raw.CommittedBy ?? raw.committedBy,
        committedOn: raw.CommittedOn ?? raw.committedOn,
        rolledBackBy: raw.RolledBackBy ?? raw.rolledBackBy,
        rolledBackOn: raw.RolledBackOn ?? raw.rolledBackOn,
        isRolledBack: raw.IsRolledBack ?? raw.isRolledBack ?? false,
    }
}

export const normalizeMigrationHistoryList = data => {
    const rows = Array.isArray(data) ? data : data?.Items ?? []
    return rows.map(normalizeMigrationHistory).filter(Boolean)
}

export const computeUnionItems = (selectedSets, canonicalChoices = {}) => {
    const itemMap = new Map()
    selectedSets.forEach(set => {
        set.items.forEach(item => {
            const text = item.displayText
            if (!text) return
            if (!itemMap.has(text)) {
                itemMap.set(text, { displayText: text, variants: [] })
            }
            itemMap.get(text).variants.push({
                templateName: set.templateName,
                score: item.score,
            })
        })
    })
    return [...itemMap.values()].map(entry => {
        const uniqueScores = [
            ...new Set(entry.variants.map(v => v.score)),
        ]
        const hasConflict = uniqueScores.length > 1
        const resolved =
            !hasConflict ||
            canonicalChoices[entry.displayText]?.score !== undefined
        const canonicalScore = hasConflict
            ? canonicalChoices[entry.displayText]?.score
            : uniqueScores[0]
        return {
            displayText: entry.displayText,
            variants: entry.variants,
            uniqueScores,
            hasConflict,
            resolved,
            canonicalScore:
                canonicalScore !== undefined ? canonicalScore : uniqueScores[0],
        }
    })
}

export const buildCanonicalItemsPayload = unionItems =>
    unionItems.map((item, idx) => ({
        DisplayText: item.displayText,
        Score: item.canonicalScore ?? 0,
        DisplayOrder: idx,
        BackColorCode: null,
        ForeColorCode: null,
        HelpText: null,
        IsScoreFromQuestion: false,
        IsExcludedFromScoring: false,
        AllowManualScore: false,
        MaxManualScore: null,
    }))

export const buildMigrationPayload = ({
    companyId,
    masterName,
    masterKey,
    selectedSets,
    unionItems,
}) => ({
    CompanyId: companyId,
    NewListName: masterName.trim(),
    NewListKey: masterKey.trim() || slugifyListKey(masterName),
    Sources: selectedSets.map(s => ({
        TemplateUniqueId: s.templateUniqueId,
        QuestionId: s.questionId,
    })),
    CanonicalItems: buildCanonicalItemsPayload(unionItems),
})

export const summarizeTemplatesForPicker = (templates, optionSets) => {
    const setsByTemplate = new Map()
    optionSets.forEach(s => {
        const tid = s.templateUniqueId
        if (!setsByTemplate.has(tid)) setsByTemplate.set(tid, [])
        setsByTemplate.get(tid).push(s)
    })
    return templates.map(t => {
        const sets = setsByTemplate.get(t.id) || []
        const questionChips = {}
        sets.forEach(s => {
            const label = s.questionText || 'Question'
            questionChips[label] = (questionChips[label] || 0) + s.items.length
        })
        const totalOptions = sets.reduce((n, s) => n + s.items.length, 0)
        return {
            id: t.id,
            name: t.name,
            totalOptions,
            questionCount: sets.length,
            chips: Object.entries(questionChips).map(([name, count]) => ({
                name,
                count,
            })),
        }
    })
}
